import { isAfter, isBefore, isDate, isValid } from 'date-fns';
import { OLDEST_VALID_DATE, YOUNGEST_VALID_DATE } from '../config'; /*

/*
  Characters exluded: ! # $ % & * ( ) _ = + [ { ] } \ | ; : "  < > , / ? @ ` ~
*/
import type { SimpleValidationRuleFunction } from '../types';
export const nameOrSurname: SimpleValidationRuleFunction<string> = (value) => {
  if (!value) {
    return true;
  }
  return /^(?:[A-Za-zÀ-ÖÙ-öù-ƿǄ-ʯ-Έ-ΊΎ-ΡΣ-ҁҊ-Ֆա-ևႠ-Ⴥა-ჺᄀ-፜፩-ᎏᵫ-ᶚḀ-‗‚-ⴀ⺀-⿕ぁ-ゖゝ-ㇿ㋿-鿯鿿-ꒌꙀ-ꙮꚀ-ꚙꜦ-ꞇꞍ-ꞿꥠ-ꥼ]| |'|-|Ά|Ό)*$/.test(
    value,
  );
};

export const italianZipCode: SimpleValidationRuleFunction<string> = (value) => {
  if (!value) {
    return true;
  }
  return /^\d{5}$/.test(value);
};

export const provinceCode: SimpleValidationRuleFunction<string> = (value) => {
  if (!value) {
    return true;
  }
  return /^[A-Z]{2}$/.test(value);
};

export const swanEmail: SimpleValidationRuleFunction<string> = (value) => {
  if (!value) {
    return true;
  }
  return /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i.test(
    value,
  );
};

//
export const taxId: SimpleValidationRuleFunction<string> = (value) => {
  if (!value) {
    return true;
  }

  // Case 1: exactly 16 characters + numbers and letters
  if (value.length === 16) {
    return /^[A-Za-z0-9]{16}$/.test(value);
  }

  // Case 2: exactly 11 characters + only numbers
  if (value.length === 11) {
    return /^[0-9]{11}$/.test(value);
  }

  return false;
};

export const url: SimpleValidationRuleFunction<string> = (value) => {
  if (!value) {
    return true;
  }
  return /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/\S*)?$/i.test(value);
};

export const vatNumber: SimpleValidationRuleFunction<string> = (value) => {
  if (!value) {
    return true;
  }
  return /^\d{11}$/.test(value);
};

export const internationalPhoneNumber: SimpleValidationRuleFunction<string> = (
  value,
) => {
  if (!value) {
    return true;
  }
  return /^\+?\d+$/.test(value);
};

export const taxIdOrUndefined: SimpleValidationRuleFunction<string> = (
  value,
) => {
  if (!value) {
    return true;
  }
  return taxId(value, []);
};

export const dateIsNotTooOld: SimpleValidationRuleFunction<string | Date> = (
  value,
) => {
  if (!value) {
    return true;
  }

  const date: Date = isDate(value)
    ? (value as Date)
    : new Date(value as string);
  if (isBefore(date, OLDEST_VALID_DATE)) {
    return false;
  }
  return true;
};

export const dateIsNotTooYoung: SimpleValidationRuleFunction<string | Date> = (
  value,
) => {
  if (!value) {
    return true;
  }

  const date: Date = isDate(value)
    ? (value as Date)
    : new Date(value as string);
  if (isAfter(date, YOUNGEST_VALID_DATE)) {
    return false;
  }
  return true;
};

export const dateMustBeAfterAnother: SimpleValidationRuleFunction<
  string | undefined,
  (string | undefined)[]
> = (a, [b]): boolean => {
  if (!a || !b) {
    return true;
  }
  return isAfter(new Date(a as string), new Date(b as string));
};

export const atLeastOneRequired: SimpleValidationRuleFunction<
  string | undefined,
  (string | undefined)[]
> = (a, [b]): boolean => {
  return Boolean(a || b);
};

export const fieldsMustBeTheSame: SimpleValidationRuleFunction<
  string | undefined,
  (string | undefined)[]
> = (a, [b]): boolean => {
  return a === b;
};

export const fieldIsRequiredIfAnother: SimpleValidationRuleFunction<
  string | undefined,
  (string | undefined)[]
> = (a, b): boolean => {
  return b[0] === b[1] ? !!a : true;
};

export const password: SimpleValidationRuleFunction<string> = (value) => {
  if (!value) {
    return true;
  }
  return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[~`!@#$%^&*()_\-+={[}\]|:;"'<,>.?/])[A-Za-z\d~`!@#$%^&*()_\-+={[}\]|:;"'<,>.?/]{8,}$/.test(
    value,
  );
};

export const date: SimpleValidationRuleFunction<Date | string> = (value) => {
  if (!value) {
    return true;
  }
  const date: Date = isDate(value)
    ? (value as Date)
    : new Date(value as string);
  return isValid(date);
};

export const time: SimpleValidationRuleFunction<string> = (value) => {
  if (!value) {
    return true;
  }
  return /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(value);
};

export const decimal: SimpleValidationRuleFunction<string> = (value) => {
  if (!value) {
    return true;
  }
  return /^\d+(\.\d+)?$/.test(String(value));
};

export const cityValidation: SimpleValidationRuleFunction<string> = (value) => {
  if (!value) {
    return true;
  }
  return /^\p{L}+(\p{L}|[0-9]|-|'|\s)*(\p{L}|[0-9])+$/u.test(value);
};

export const arrayValuesMatchRegex: SimpleValidationRuleFunction<
  string[] | { label: string; value: string }[],
  [string | RegExp]
> = (values, [regex]) => {
  if (!values || values.length === 0) {
    return true;
  }

  return values.every((value) => {
    if (!value) {
      return true;
    }
    if (typeof value === 'string') {
      return new RegExp(regex).test(value);
    } else {
      return new RegExp(regex).test(value.value);
    }
  });
};

export const noBackslash: SimpleValidationRuleFunction<string> = (value) => {
  if (!value) {
    return true;
  }
  return !value.includes('\\');
};

export const objectValueEmail: SimpleValidationRuleFunction<
  { label: string; value: string; internalValue: string } | undefined
> = (value) => {
  if (!value?.internalValue) {
    return true;
  }
  return swanEmail(value.internalValue, []) === true;
};
